import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, Button, Card, InputGroup } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Eye from "../../assets/Eye.svg";
import EyeOff from "../../assets/Eyeoff.svg";
import { ConfigProvider, Input } from "antd";
import { registerApi, verifyEmailApi, getAppSettings } from "../../api/api";
import { useNavigate, useLocation } from "react-router-dom";
import "./register.css";
import "./login.css"; // reuse shared auth styles (logo, alerts, field-error)
import { useDispatch } from "react-redux";
import { LOGIN_SUCCESS } from "../../redux/actions/action";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import brandLogo from "../../assets/image/logo.e3c0b2196cc23f84f67a.png";

import { handleError } from "../../helpers/handleError";
import { urlDelPortal } from "../../helpers/zoco";

const RegisterForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location && location.state && location.state.from) || "";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpStatus, setOtpStatus] = useState("idle"); // idle | invalid | expired | success
  const dispatch = useDispatch();
  const [otpError, setOtpError] = useState("");
  const [regestraError, setRegestraError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    dni: "",
  });
  const [isOtp, setIsOtp] = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm();
  const passwordValue = watch("password") || "";
  const pwHasUpper = /[A-Z]/.test(passwordValue);
  const pwHasLower = /[a-z]/.test(passwordValue);
  const pwHasNumber = /\d/.test(passwordValue);
  const pwHasSpecial = /[^A-Za-z0-9]/.test(passwordValue);
  const [timeLeft, setTimeLeft] = useState(59);

  useEffect(() => {
    if (timeLeft === 0) return; // no disparar estado 'expired' automáticamente
    const timerId = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);

  // Branding (logo) like login
  useEffect(() => {
    (async () => {
      try {
        const res = await getAppSettings();
        if (res.status === 200) setAppSettings(res.data.info || {});
      } catch {}
    })();
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setRegestraError("");
    try {
      const dniValue = String(data.dni || "").trim();
      const requestData = {
        name: data.username,
        email: data.email,
        password: data.password,
        confpassword: data.confirmPassword,
      };
      if (dniValue) requestData.dni = dniValue;
      const response = await registerApi(requestData);
      if (response.status === 200) {
        setIsOtp(true);
        setFormData({ ...formData, ...data });
        setTimeLeft(59);
        reset();
      }
    } catch (error) {
      handleError(error);
      setRegestraError(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    setIsLoading(true);
    try {
      const dniValue = String(formData.dni || "").trim();
      const requestData = {
        name: formData.username,
        email: formData.email,
        password: formData.password,
        confpassword: formData.confirmPassword,
      };
      if (dniValue) requestData.dni = dniValue;
      const response = await registerApi(requestData);
      if (response.status === 200) {
        setTimeLeft(59);
        setOtp("");
        setOtpStatus('idle');
        setOtpError("");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (text) => {
    setOtp(text);
    if (otpStatus !== 'success') {
      setOtpStatus('idle');
      setOtpError("");
    }
  };

  const submitOtp = async () => {
    if (otp === "") { setOtpError("El código OTP es obligatorio"); return; }
    if (otp.length !== 4) { setOtpError("El código OTP debe tener 4 dígitos"); return; }
    if (timeLeft === 0) { setOtpStatus('expired'); setOtpError('Este código expiró. Pedí uno nuevo'); return; }
    try {
      setIsLoading(true);
      const requestData = { email: formData.email, otp };
      if (otpError !== "") setOtpError("");
      const response = await verifyEmailApi(requestData);
      if (response.status === 200) {
        setOtpStatus('success');
        setTimeout(() => navigate('/login', { state: { from: fromPath || '' } }), 3500);
      }
    } catch (error) {
      handleError(error);
      setOtpStatus('invalid');
      setOtpError(error?.response?.data?.message || "El código no es correcto. Intentalo de nuevo");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);
  if (isLoading) return <SpinLoading />;

  return (
    <>
      {isOtp ? (
        <div className="register-container pading150">
          <div className="login-logo-wrap">
            <img
              className="login-logo"
              src={appSettings.logo || brandLogo}
              alt="Zoco"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/event')}
            />
          </div>
          {(otpStatus === 'invalid') && (
            <div className="login-alert" style={{ marginTop: -24, marginBottom: 16 }}>
              <span className="icon" style={{ color: "#E53935" }}>i</span>
              <div>El código no es correcto. Intentalo de nuevo</div>
            </div>
          )}
          {(otpStatus === 'expired') && (
            <div className="login-alert" style={{ marginTop: -24, marginBottom: 16 }}>
              <span className="icon" style={{ color: "#E53935" }}>i</span>
              <div>
                Este código expiró. <button type="button" className="link-btn red" onClick={() => resendOtp()}>Pedí uno nuevo</button>
              </div>
            </div>
          )}
          {otpStatus !== 'success' ? (
          <Card className="register-card">
            <Card.Body>
              <h2 className="register-title">Verifica tu identidad</h2>
              <p className="otp-verify-subtext">Ingresá el código de 4 dígitos enviado a tu casilla de email para continuar</p>

              <Form className="otp-verify-form">
                <ConfigProvider theme={{ components: { Input: { activeBorderColor: "#EC6325", activeBg: "#EAF0F5" } }, token: { colorBgContainer: "#C7D5E1" } }}>
                  <div className={`otp-inputs ${otpStatus === 'invalid' ? 'error' : ''}`}>
                    <Input.OTP variant="filled" length={4} onChange={onChange}  />
                  </div>
                  {otpError !== "" && otpStatus !== 'expired' && (
                    <p className="field-error" style={{ marginTop: 10 }}>{otpError}</p>
                  )}
                </ConfigProvider>

                <span className="otp-verify-timer">00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}</span>
              </Form>
              <Button className="otp-verify-button" onClick={() => submitOtp()} disabled={isLoading}>
                {isLoading ? (<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>) : ("Verificar")}
              </Button>
              <p className="otp-verify-resend">
                ¿No recibiste el código? <a style={{ cursor: "pointer" }} onClick={() => resendOtp()}>Reenviar</a>
              </p>
            </Card.Body>
          </Card>
          ) : (
            <Card className="register-card">
              <Card.Body style={{ paddingTop: 40, paddingBottom: 40 }}>
                <div className="otp-success-icon">✓</div>
                <h2 className="otp-success-title">Cuenta verificada con éxito</h2>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                <p className="otp-success-subtext">Ya podés iniciar sesión para seguir explorando eventos y comprar tus tickets de forma rápida y segura</p>
                </div>
                <Button className="otp-verify-button" onClick={() => navigate('/login')}>Ir a iniciar sesión</Button>
              </Card.Body>
            </Card>
          )}
        </div>
      ) : (
        <div className="register-container pading150">
          <div className="login-logo-wrap">
            <img
              className="login-logo"
              src={appSettings.logo || brandLogo}
              alt="Zoco"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/event')}
            />
          </div>
          <Card className="register-card">
            <Card.Body style={{ padding: "0px" }}>
              <h2 className="register-title">Registra tu cuenta</h2>
              <p className="register-subtext">Y conseguí entradas para tus eventos favoritos, sin cargos de servicio</p>

              {(Object.keys(errors || {}).length > 0 || regestraError) && (
                <div className="login-alert" style={{ marginBottom: 16 }}>
                  <span className="icon" style={{ color: "#E53935" }}>i</span>
                  <div>
                    {Object.keys(errors || {}).length > 0 ? (
                      <>Campo obligatorio</>
                    ) : (
                      <>
                        {regestraError}
                        {typeof regestraError === 'string' && regestraError.toLowerCase().includes('cuenta') && (
                          <button type="button" className="link-btn red" onClick={() => navigate('/login', { state: { from: fromPath || '' } })}> Inicia sesión acá</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              <Form onSubmit={handleSubmit(onSubmit)}>
                {/* Username Field */}
                <Form.Group className="mb-4">
                  <Form.Control
                    type="text"
                    placeholder="Usuario *"
                    {...register("username", { required: "El nombre de usuario es obligatorio" })}
                    className={`register-input ${errors.username ? 'error' : ''}`}
                  />
                  {errors.username && <p className="field-error">{errors.username.message}</p>}
                </Form.Group>

                {/* Email Field */}
                <Form.Group className="mb-4">
                  <Form.Control
                    type="email"
                    placeholder="Email *"
                    {...register("email", {
                      required: "El correo electrónico es obligatorio",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Formato de correo electrónico inválido" },
                    })}
                    className={`register-input ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <p className="field-error">{errors.email.message}</p>}
                </Form.Group>

                {/* Password Field */}
                <Form.Group className="mb-4 position-relative" onFocus={() => setPasswordFocused(true)} onBlur={() => setPasswordFocused(false)}>
                  <InputGroup className={`register-input-group ${errors.password ? 'error' : ''}`}>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña *"
                      {...register("password", {
                        required: "La Contraseña es obligatoria",
                        minLength: { value: 8, message: "La Contraseña debe tener al menos 8 caracteres" },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                          message: "La Contraseña debe contener al menos 8 caracteres, 1 letra mayúscula, 1 letra minúscula, 1 número, 1 carácter especial",
                        },
                      })}
                      className="register-input-password"
                    />
                    <span onClick={() => setShowPassword(!showPassword)} className="register-eye">
                      <img src={showPassword ? EyeOff : Eye} alt="Mostrar/Ocultar Contraseña" width={20} height={20} />
                    </span>
                  </InputGroup>
                  {passwordFocused && (
                    <div className="password-conditions" aria-live="polite">
                      <span className={`cond-item ${pwHasUpper ? 'ok' : ''}`}>Mayuscula</span>
                      <span className={`cond-item ${pwHasLower ? 'ok' : ''}`}>Minuscula</span>
                      <span className={`cond-item ${pwHasNumber ? 'ok' : ''}`}>Numero</span>
                      <span className={`cond-item ${pwHasSpecial ? 'ok' : ''}`}>Caracter especial</span>
                    </div>
                  )}
                  {errors.password && (
                    <div className="field-error">
                      {errors.password.message.split("\n").map((line, index) => (
                        <p key={index} className="mb-1">{line}</p>
                      ))}
                    </div>
                  )}
                </Form.Group>

                {/* Confirm Password Field */}
                <Form.Group className="mb-4 position-relative">
                  <InputGroup className={`register-input-group ${errors.confirmPassword ? 'error' : ''}`}>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirmar Contraseña *"
                      {...register("confirmPassword", {
                        required: "La confirmación de Contraseña es obligatoria",
                        validate: (value) => value === watch("password") || "Las Contraseñas no coinciden",
                      })}
                      className="register-input-password"
                    />
                    <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="register-eye">
                      <img src={showConfirmPassword ? EyeOff : Eye} alt="EyeOff" />
                    </span>
                  </InputGroup>
                  {errors.confirmPassword && <p className="field-error">{errors.confirmPassword.message}</p>}
                </Form.Group>

                {/* DNI Field */}
                <Form.Group className="mb-4">
                  <Form.Control
                    type="text"
                    placeholder="DNI (opcional)"
                    {...register("dni", {
                      pattern: { value: /^\d{7,10}$/, message: "El DNI debe ser numérico (7 a 10 dígitos)" },
                    })}
                    className={`register-input ${errors.dni ? 'error' : ''}`}
                  />
                  {errors.dni && <p className="field-error">{errors.dni.message}</p>}
                </Form.Group>

                {regestraError !== "" && <p className="field-error">{regestraError}</p>}

                {/* Submit Button */}
                <Button type="submit" className="register-button" disabled={isLoading}>
                  {isLoading ? (<SpinLoading />) : ("Registrarse")}
                </Button>

                <div className="register-links-row">
                  <span>¿Ya tenés cuenta?</span>
                  <button type="button" className="link-btn" onClick={() => navigate("/login", { state: { from: fromPath || '' } })}>
                    Inicia sesión
                  </button>
                </div>

                <div className="register-links-row">
                  <span>¿Querés vender entradas?</span>
                  <a className="link-btn" href={urlDelPortal("/entrar")}>Entrá por ZOCO</a>
                </div>

                <p className="register-legal">
                  Al crear tu cuenta aceptás nuestros
                  <button type="button" className="link-btn" onClick={() => navigate('/termsandcondition')}> Términos de uso</button>
                  y
                  <button type="button" className="link-btn" onClick={() => navigate('/privacypolicy')}> Política de privacidad</button>
                </p>
              </Form>
            </Card.Body>
          </Card>
        </div>
      )}
    </>
  );
};

export default RegisterForm;


